export const deepLinkEvent = "openanalyst:deep-link"

const acceptedSchemes = new Set(["openanalyst:", "openanalyst-local:", "openanalyst:"])

type ProjectDeepLinkTarget =
  | {
      directory: string
      workspaceId?: undefined
    }
  | {
      directory?: undefined
      workspaceId: string
    }

export type OpenProjectDeepLink = ProjectDeepLinkTarget & {
  desktopId?: string
  handle?: string
  smartwork?: boolean
}

export type NewSessionDeepLink = OpenProjectDeepLink & {
  prompt?: string
}

export type MissingLocalProjectDeepLinkAction = "open-project" | "new-session"

export type MissingLocalProjectDeepLinkIntent = {
  workspaceId: string
  desktopId?: string
  handle?: string
  action: MissingLocalProjectDeepLinkAction
  prompt?: string
  smartwork?: boolean
}

export type LoginDeepLink = {
  authIntentId?: string
  // Self-contained starter link (openanalyst://login?starter=<templateId>) — no
  // 10x-minted authIntentId required. Mirrors the web `?starter=` param.
  starter?: string
}

export type BillingDeepLink = {
  status: "success" | "cancel"
  sessionId?: string
}

const parseUrl = (input: string) => {
  if (typeof URL.canParse === "function" && !URL.canParse(input)) return
  try {
    const url = new URL(input)
    if (!acceptedSchemes.has(url.protocol)) return
    return url
  } catch {
    return
  }
}

const smartworkMode = (url: URL) => url.searchParams.get("smartwork") === "1"

const projectTarget = (url: URL): ProjectDeepLinkTarget | undefined => {
  const directory = url.searchParams.get("directory")
  if (directory) return { directory }
  const workspaceId = url.searchParams.get("workspaceId")?.trim()
  if (!workspaceId) return
  return { workspaceId }
}

const projectLink = (url: URL): OpenProjectDeepLink | undefined => {
  const target = projectTarget(url)
  if (!target) return
  const desktopId = url.searchParams.get("desktopId")?.trim()
  const handle = url.searchParams.get("handle")?.trim().replace(/^@/, "")
  if ("workspaceId" in target && !desktopId) return
  return {
    ...target,
    ...(desktopId ? { desktopId } : {}),
    ...(handle ? { handle } : {}),
    ...(smartworkMode(url) ? { smartwork: true } : {}),
  }
}

export const parseDeepLink = (input: string): OpenProjectDeepLink | undefined => {
  const url = parseUrl(input)
  if (!url) return
  if (url.hostname !== "open-project") return
  return projectLink(url)
}

export const parseNewSessionDeepLink = (input: string): NewSessionDeepLink | undefined => {
  const url = parseUrl(input)
  if (!url) return
  if (url.hostname !== "new-session") return
  const link = projectLink(url)
  if (!link) return
  const prompt = url.searchParams.get("prompt") || undefined
  if (prompt) return { ...link, prompt }
  return link
}

export const parseLoginDeepLink = (input: string): LoginDeepLink | undefined => {
  const url = parseUrl(input)
  if (!url) return
  if (url.hostname !== "login") return
  const authIntentId = url.searchParams.get("authIntentId")?.trim()
  const starter = url.searchParams.get("starter")?.trim()
  if (!authIntentId && !starter) return
  return {
    ...(authIntentId ? { authIntentId } : {}),
    ...(starter ? { starter } : {}),
  }
}

export const parseBillingDeepLink = (input: string): BillingDeepLink | undefined => {
  const url = parseUrl(input)
  if (!url) return
  if (url.hostname !== "billing") return
  const status = url.pathname === "/cancel" ? "cancel" : url.pathname === "/success" ? "success" : null
  if (!status) return
  const sessionId = url.searchParams.get("session_id")?.trim()
  return {
    status,
    ...(sessionId ? { sessionId } : {}),
  }
}

export const collectOpenProjectDeepLinks = (urls: string[]) =>
  urls.map(parseDeepLink).filter((link): link is OpenProjectDeepLink => !!link)

export const collectNewSessionDeepLinks = (urls: string[]) =>
  urls.map(parseNewSessionDeepLink).filter((link): link is NewSessionDeepLink => !!link)

export const collectLoginDeepLinks = (urls: string[]) =>
  urls.map(parseLoginDeepLink).filter((link): link is LoginDeepLink => !!link)

export const collectBillingDeepLinks = (urls: string[]) =>
  urls.map(parseBillingDeepLink).filter((link): link is BillingDeepLink => !!link)

export const billingDeepLinkPath = (link: BillingDeepLink) => {
  const search = new URLSearchParams({ view: "billing", billingReturn: link.status })
  if (link.sessionId) search.set("session_id", link.sessionId)
  return `/smartwork?${search.toString()}`
}

export const missingLocalProjectDeepLinkPath = (input: {
  workspaceId: string
  desktopId?: string | null
  handle?: string | null
  action?: MissingLocalProjectDeepLinkAction
  prompt?: string
  smartwork?: boolean
}) => {
  const search = new URLSearchParams({ workspace: input.workspaceId, missingLocalProject: "1" })
  const handle = input.handle?.trim().replace(/^@/, "")
  if (input.desktopId?.trim()) search.set("desktopId", input.desktopId.trim())
  if (handle) search.set("handle", handle)
  if (input.action) search.set("action", input.action)
  if (input.prompt) search.set("prompt", input.prompt)
  if (input.smartwork) search.set("smartwork", "1")
  return `/smartwork?${search.toString()}`
}

export const missingLocalProjectDeepLinkIntent = (search: string): MissingLocalProjectDeepLinkIntent | null => {
  const params = new URLSearchParams(search)
  if (params.get("missingLocalProject") !== "1") return null
  const workspaceId = params.get("workspace")?.trim()
  if (!workspaceId) return null
  const action = params.get("action") === "new-session" ? "new-session" : "open-project"
  const prompt = params.get("prompt") || undefined
  const desktopId = params.get("desktopId")?.trim()
  const handle = params.get("handle")?.trim().replace(/^@/, "")
  return {
    workspaceId,
    action,
    ...(desktopId ? { desktopId } : {}),
    ...(handle ? { handle } : {}),
    ...(prompt ? { prompt } : {}),
    ...(params.get("smartwork") === "1" ? { smartwork: true } : {}),
  }
}

export type DeepLinkWorkspaceResolution =
  | { kind: "found"; record: { workspacePath: string } }
  | { kind: "not-found" }
  | {
      kind: "ambiguous"
      candidates: Array<{ workspacePath: string; desktopId?: string; handle?: string }>
    }

export const resolveProjectDeepLinkDirectory = (
  link: OpenProjectDeepLink | NewSessionDeepLink,
  resolveWorkspace: (input: {
    workspaceId: string
    desktopId?: string | null
    handle?: string | null
  }) => DeepLinkWorkspaceResolution | { workspacePath: string } | null,
) => {
  if (link.directory) return { kind: "directory" as const, directory: link.directory }
  const workspaceId = link.workspaceId?.trim()
  if (!workspaceId) return null
  const resolver = resolveWorkspace({ desktopId: link.desktopId, workspaceId, ...(link.handle ? { handle: link.handle } : {}) })
  if (!resolver) {
    return {
      kind: "missing-local-project" as const,
      workspaceId,
      ...(link.desktopId ? { desktopId: link.desktopId } : {}),
      ...(link.handle ? { handle: link.handle } : {}),
    }
  }
  // Legacy resolver shape — direct record-like object.
  if (!("kind" in resolver)) {
    return { kind: "directory" as const, directory: resolver.workspacePath }
  }
  if (resolver.kind === "found") {
    return { kind: "directory" as const, directory: resolver.record.workspacePath }
  }
  if (resolver.kind === "ambiguous") {
    return {
      kind: "ambiguous-local-project" as const,
      workspaceId,
      candidates: resolver.candidates,
      ...(link.desktopId ? { desktopId: link.desktopId } : {}),
      ...(link.handle ? { handle: link.handle } : {}),
    }
  }
  return {
    kind: "missing-local-project" as const,
    workspaceId,
    ...(link.desktopId ? { desktopId: link.desktopId } : {}),
    ...(link.handle ? { handle: link.handle } : {}),
  }
}

export const sessionDeepLinkSearch = (link: Pick<NewSessionDeepLink, "prompt" | "smartwork">) => {
  const search = new URLSearchParams()
  if (link.prompt) search.set("prompt", link.prompt)
  if (link.smartwork) search.set("smartwork", "1")
  const query = search.toString()
  return query ? `?${query}` : ""
}

type OpenAnalystWindow = Window & {
  __OPENANALYST__?: {
    deepLinks?: string[]
  }
}

export const drainPendingDeepLinks = (target: OpenAnalystWindow, predicate?: (url: string) => boolean) => {
  const pending = target.__OPENANALYST__?.deepLinks ?? []
  if (pending.length === 0) return []
  if (!predicate) {
    if (target.__OPENANALYST__) target.__OPENANALYST__.deepLinks = []
    return pending
  }
  const matched = pending.filter(predicate)
  if (target.__OPENANALYST__) target.__OPENANALYST__.deepLinks = pending.filter((url) => !predicate(url))
  return matched
}
